import {
  formatDateTime,
  HISTORY_WINDOWS,
  kpiChangesSchema,
  kpiHistorySchema,
  PROJECTION_METHOD_LABELS,
  TRAFFIC_LIGHT_LABELS,
  TREND_LABELS,
  unitComparisonKpisSchema,
  type HistoryWindow,
  type KpiHistory,
} from '@fluxus/shared';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { FolderKanban, History, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { useId, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/select';
import { AUDIT_SOURCE_LABELS as SOURCE_LABELS } from '@/features/audit/labels';
import { CollaborationPanel } from '@/features/collaboration/CollaborationPanel';
import { DecisionHistoryList } from '@/features/meetings/Minutes';
import { DRILL_PARAMS, drillHref, readId, readPeriod, type DrillPath } from '@/features/drill/drill';
import { DrillTrail } from '@/features/drill/DrillTrail';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

const formatValue = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
const withUnit = (v: number | null, unit: string) =>
  v === null ? '—' : unit === 'R$' ? `R$ ${formatValue(v)}` : `${formatValue(v)} ${unit}`;
const percent = (v: number | null) => (v === null ? null : `${formatValue(v)}%`);

const ENTITY_LABELS: Record<string, string> = {
  kpi: 'Cadastro',
  kpi_target: 'Meta',
  kpi_result: 'Realizado',
};
const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/**
 * Indicadores › Histórico (T-069, RF-27, RN-05, PRD §11.15): resultado e meta atuais, performance, tendência,
 * média, acumulado, melhor e pior período e projeção; gráfico de 12, 24 ou 36 meses; e as alterações do KPI.
 */
export function HistoricoIndicadorPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  return (
    <PageContainer className="max-w-6xl">
      <PageHeader
        title="Histórico do indicador"
        description="O caminho de um indicador ao longo do tempo: resultados contra a meta, tendência, melhor e pior período, projeção do ano e cada alteração feita nele."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="Os indicadores pertencem a um projeto. Quando a empresa tiver um projeto em andamento, eles aparecem aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <div key={project.id} className="mt-4 flex flex-col gap-4">
          <ProjectPicker projects={projects} project={project} onSelect={select} />
          <HistorySection organizationId={organizationId} projectId={project.id} />
        </div>
      )}
    </PageContainer>
  );
}

function HistorySection({ organizationId, projectId }: { organizationId: string; projectId: string }) {
  const { client } = useAuth();
  const can = useCan();
  const [params, setParams] = useSearchParams();
  const [months, setMonths] = useState<HistoryWindow>(12);
  const ids = { kpi: useId() };
  const projectBase = `/organizations/${organizationId}/projects/${projectId}`;
  const kpis = useQuery({
    queryKey: ['history', projectId, 'kpis'],
    queryFn: ({ signal }) =>
      client.request(`${projectBase}/results/by-unit/kpis`, {
        query: { archived: 'include' },
        schema: unitComparisonKpisSchema,
        signal,
      }),
  });
  // O histórico mostra metas e resultados de todas as unidades: só os KPIs acompanhados pela área — os
  // arquivados também (o histórico continua consultável, RN-05).
  const options = (kpis.data?.items ?? [])
    .filter((k) => k.access === 'AREA')
    .sort((a, b) => Number(a.archived) - Number(b.archived));
  const requested = params.get('kpi');
  // Indicador do link que não está nas opções (removido ou fora do acesso): avisa em vez de trocar de KPI.
  const missing = Boolean(requested) && Boolean(kpis.data) && !options.some((k) => k.id === requested);
  const kpiId = missing ? '' : (options.find((k) => k.id === requested)?.id ?? options[0]?.id ?? '');
  // Caminho do drill-down (T-144b, DEC-058): o objetivo de onde se veio vale para o indicador do link.
  const period = readPeriod(params);
  const path: DrillPath = {
    projectId,
    kpiId,
    objectiveId: kpiId === requested ? readId(params, DRILL_PARAMS.objective) : null,
    year: period?.year ?? null,
    month: period?.month ?? null,
  };
  const data = useQuery({
    queryKey: ['history', projectId, kpiId, months],
    queryFn: ({ signal }) =>
      client.request(`${projectBase}/kpis/${kpiId}/history`, {
        query: { months: String(months) },
        schema: kpiHistorySchema,
        signal,
      }),
    enabled: Boolean(kpiId),
    placeholderData: keepPreviousData,
  });

  if (kpis.isPending) return <LoadingState rows={4} label="Carregando os indicadores" />;
  if (kpis.isError) {
    return <ErrorState error={kpis.error} onRetry={() => void kpis.refetch()} retrying={kpis.isFetching} />;
  }
  if (!options.length) {
    return (
      <EmptyState
        className="rounded-lg border border-dashed border-border py-12"
        icon={History}
        title="Nenhum indicador para acompanhar"
        description="Os indicadores das suas áreas aparecem aqui com o histórico de resultados e de alterações."
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="sm:w-96">
          <label htmlFor={ids.kpi} className="mb-1 block text-xs font-medium text-muted-foreground">
            Indicador
          </label>
          <NativeSelect
            id={ids.kpi}
            value={kpiId}
            // Outro indicador: o projeto e o período continuam; o objetivo do caminho anterior sai.
            onChange={(e) =>
              setParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  next.set(DRILL_PARAMS.kpi, e.target.value);
                  next.delete(DRILL_PARAMS.objective);
                  return next;
                },
                { replace: true },
              )
            }
          >
            {missing ? <option value="">Escolha um indicador</option> : null}
            {options.map((k) => (
              <option key={k.id} value={k.id}>
                {k.code} — {k.name}
                {k.archived ? ' (arquivado)' : ''}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div
          role="group"
          aria-label="Período do gráfico"
          className="inline-flex rounded-md border border-border p-0.5"
        >
          {HISTORY_WINDOWS.map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={months === m}
              onClick={() => setMonths(m)}
              className={cn(
                'rounded px-3 py-1 text-xs font-medium',
                months === m
                  ? 'bg-brand-soft text-brand-strong dark:text-brand'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {m} meses
            </button>
          ))}
        </div>
      </div>
      {kpiId ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <DrillTrail organizationId={organizationId} path={path} />
          <Button asChild variant="outline" size="sm" className="self-start sm:self-auto">
            <Link to={drillHref.unit(organizationId, path)}>Ver por unidade</Link>
          </Button>
        </div>
      ) : null}
      {missing ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={History}
          title="Indicador não disponível"
          description="O indicador do link não existe neste projeto ou não está no seu acesso. Escolha outro na lista acima."
        />
      ) : data.isPending ? (
        <LoadingState rows={4} label="Carregando o histórico" />
      ) : data.isError ? (
        <ErrorState error={data.error} onRetry={() => void data.refetch()} retrying={data.isFetching} />
      ) : (
        <HistoryView data={data.data} busy={data.isFetching} />
      )}
      {kpiId ? <ChangesSection base={`${projectBase}/kpis/${kpiId}/history/changes`} kpiId={kpiId} /> : null}
      {kpiId && can('analise-critica', 'view') ? (
        <section aria-label="Histórico de decisões" className="flex flex-col gap-3">
          <h2 className="text-base font-bold">Histórico de decisões</h2>
          <p className="text-sm text-muted-foreground">
            O que as análises críticas decidiram sobre o indicador e se ele melhorou depois do prazo.
          </p>
          <DecisionHistoryList
            key={kpiId}
            organizationId={organizationId}
            projectId={projectId}
            subject="kpis"
            subjectId={kpiId}
            label={options.find((k) => k.id === kpiId)?.name ?? 'o indicador'}
          />
        </section>
      ) : null}
      {kpiId ? (
        <div className="rounded-lg border border-border bg-surface p-4">
          <CollaborationPanel
            key={kpiId}
            organizationId={organizationId}
            projectId={projectId}
            subject="kpi"
            subjectId={kpiId}
            label={options.find((k) => k.id === kpiId)?.name ?? 'o indicador'}
          />
        </div>
      ) : null}
    </div>
  );
}

function HistoryView({ data, busy }: { data: KpiHistory; busy: boolean }) {
  const s = data.stats;
  const unit = data.kpi.unitLabel;
  const TrendIcon =
    s.trend?.direction === 'UP' ? TrendingUp : s.trend?.direction === 'DOWN' ? TrendingDown : Minus;
  const range = `${MONTHS[data.window.from.month - 1]}/${data.window.from.year} a ${MONTHS[data.window.to.month - 1]}/${data.window.to.year}`;
  return (
    <div className="flex flex-col gap-5" aria-busy={busy}>
      <section
        aria-label="Indicadores do histórico"
        className="rounded-lg border border-border bg-surface p-4"
      >
        <h2 className="text-sm font-semibold">
          {data.kpi.code} — {data.kpi.name}
        </h2>
        <dl className="mt-3 grid gap-x-6 gap-y-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Fact label={s.current ? `Resultado atual (${s.current.label})` : 'Resultado atual'}>
            {withUnit(s.current?.actual ?? null, unit)}
            {s.current ? (
              <StatusBadge
                status={s.current.light}
                label={percent(s.current.performance) ?? TRAFFIC_LIGHT_LABELS[s.current.light]}
                size="sm"
              />
            ) : null}
          </Fact>
          <Fact label={s.currentTarget ? `Meta atual (${s.currentTarget.label})` : 'Meta atual'}>
            {withUnit(s.currentTarget?.value ?? null, unit)}
          </Fact>
          <Fact label="Tendência (últimos 6 períodos)">
            {s.trend ? (
              <span className="inline-flex items-center gap-1">
                <TrendIcon className="size-4" aria-hidden />
                {TREND_LABELS[s.trend.direction]}
                {s.trend.favorable === null ? '' : s.trend.favorable ? ' · favorável' : ' · desfavorável'}
              </span>
            ) : (
              'Poucos resultados'
            )}
          </Fact>
          <Fact label="Média no período">{withUnit(s.average, unit)}</Fact>
          <Fact label={s.ytd ? `Acumulado de ${s.ytd.year}` : 'Acumulado do ano'}>
            {s.ytd ? `${withUnit(s.ytd.actual, unit)} de ${withUnit(s.ytd.target, unit)}` : '—'}
            {s.ytd ? (
              <StatusBadge
                status={s.ytd.light}
                label={percent(s.ytd.performance) ?? TRAFFIC_LIGHT_LABELS[s.ytd.light]}
                size="sm"
              />
            ) : null}
          </Fact>
          <Fact label="Melhor período">
            {s.best
              ? `${s.best.label} · ${withUnit(s.best.actual, unit)} (${percent(s.best.performance)})`
              : '—'}
          </Fact>
          <Fact label="Pior período">
            {s.worst
              ? `${s.worst.label} · ${withUnit(s.worst.actual, unit)} (${percent(s.worst.performance)})`
              : '—'}
          </Fact>
          <Fact label="Projeção de fechamento do ano">
            {s.projections.some((p) => p.closing !== null)
              ? s.projections
                  .filter((p) => p.closing !== null)
                  .map(
                    (p) =>
                      `${PROJECTION_METHOD_LABELS[p.method]}: ${withUnit(p.closing, unit)}${
                        p.closingPerformance !== null ? ` (${formatValue(p.closingPerformance)}%)` : ''
                      }`,
                  )
                  .join(' · ')
              : 'Sem realizado no ano'}
          </Fact>
        </dl>
      </section>

      <section aria-label="Gráfico do histórico" className="rounded-lg border border-border bg-surface p-4">
        <h2 className="text-sm font-semibold">Realizado x meta · {range}</h2>
        {data.points.some((p) => p.actual !== null || p.target !== null) ? (
          <div
            className="mt-3 h-72 w-full"
            role="img"
            aria-label={`Gráfico de ${data.window.months} meses — ${data.points
              .map(
                (p) => `${p.label}: realizado ${withUnit(p.actual, unit)}, meta ${withUnit(p.target, unit)}`,
              )
              .join('; ')}.`}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data.points.map((p) => ({ name: p.label, realizado: p.actual, meta: p.target }))}
                margin={{ top: 8, right: 8, bottom: 8, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  minTickGap={12}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                  width={64}
                  tickFormatter={(v: number) => formatValue(v)}
                />
                <Tooltip formatter={(v) => (typeof v === 'number' ? withUnit(v, unit) : '—')} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="meta"
                  name="Meta"
                  stroke="var(--muted-foreground)"
                  strokeDasharray="5 4"
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="realizado"
                  name="Realizado"
                  stroke="var(--brand)"
                  strokeWidth={2.5}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">Nenhum resultado nem meta neste período.</p>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          {data.kpi.periodicity === 'MONTHLY'
            ? 'Um ponto por mês.'
            : 'Um ponto por período do indicador (o mês em que ele fecha).'}
          {data.computedAt ? ` Calculado em ${formatDateTime(data.computedAt)}.` : ''}
        </p>
      </section>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 flex flex-wrap items-center gap-2 font-semibold tabular-nums">{children}</dd>
    </div>
  );
}

function ChangesSection({ base, kpiId }: { base: string; kpiId: string }) {
  const { client } = useAuth();
  const changes = useInfiniteQuery({
    queryKey: ['history', 'changes', kpiId],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      client.request(base, {
        query: { limit: '30', ...(pageParam ? { cursor: pageParam } : {}) },
        schema: kpiChangesSchema,
        signal,
      }),
    getNextPageParam: (last) => last.nextCursor,
  });
  const items = changes.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <section aria-label="Alterações do indicador" className="flex flex-col gap-3">
      <h2 className="text-base font-bold">Histórico de alterações</h2>
      {changes.isPending ? (
        <LoadingState rows={3} label="Carregando as alterações" />
      ) : changes.isError ? (
        <ErrorState
          error={changes.error}
          onRetry={() => void changes.refetch()}
          retrying={changes.isFetching}
        />
      ) : !items.length ? (
        <p className="text-sm text-muted-foreground">Nenhuma alteração registrada para este indicador.</p>
      ) : (
        <>
          <ol
            aria-label="Alterações, mais recentes primeiro"
            className="flex flex-col divide-y divide-border rounded-lg border border-border bg-surface"
          >
            {items.map((c) => {
              return (
                <li key={c.id} className="px-4 py-3 text-sm">
                  <p className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{ENTITY_LABELS[c.entity] ?? c.entity}</Badge>
                    <span className="font-semibold">
                      {c.fieldLabel ?? c.action.toLowerCase()}
                      {c.periodLabel ? ` · ${c.periodLabel}` : ''}
                      {c.unitName ? ` · ${c.unitName}` : ''}
                    </span>
                    {c.closedPeriod ? <Badge variant="brand">Período encerrado</Badge> : null}
                  </p>
                  <p className="mt-1 tabular-nums">
                    {c.oldDisplay ?? '—'} → {c.newDisplay ?? '—'}
                  </p>
                  {c.reason ? <p className="mt-1 text-xs">Motivo: {c.reason}</p> : null}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {c.actorName ?? 'Sistema'} · {formatDateTime(c.occurredAt)} ·{' '}
                    {SOURCE_LABELS[c.source] ?? c.source}
                  </p>
                </li>
              );
            })}
          </ol>
          {changes.hasNextPage ? (
            <Button
              variant="outline"
              className="self-start"
              loading={changes.isFetchingNextPage}
              onClick={() => void changes.fetchNextPage()}
            >
              Carregar mais
            </Button>
          ) : null}
        </>
      )}
    </section>
  );
}
